import React from 'react';

import PropTypes from 'prop-types';

import { VALID_DATE_FORMATS } from '@adsk/alloy-react-datetime';
import { useForwardedRef } from '@adsk/alloy-react-helpers';
import { useTheme } from '@adsk/alloy-react-styles';
import { StylableComponent } from '@adsk/alloy-react-theme';

import { DATE_PICKER_VARIANTS } from './consts';
import { defaultRenderTrigger } from './helpers/defaultRenderers';
import useDateRangePicker from './hooks/useDateRangePicker';
import Footer from './layout/Footer';
import DatePickerOverlay from './layout/Overlay';
import PickerOverlayPortal from './layout/PickerOverlayPortal';
import Trigger from './layout/Trigger';
import { datePickerCommonPropTypes } from './propTypes/datePickerCommonPropTypes';
import type { DatePickerCommonArgs, DateRangePickerArgs } from './types';

type DateRangePickerProps = DateRangePickerArgs &
  DatePickerCommonArgs &
  Omit<StylableComponent<HTMLDivElement>, 'onChange'>;
/**
 * The Date Range Picker is a basic component that allows the user to pick a date range
 * Date pickers allow the selection of specific dates and years. It displays a default
 * text field and a dropdown calendar appears when a user taps on the text field.
 */
const DateRangePicker = React.forwardRef<HTMLDivElement, DateRangePickerProps>(
  (
    {
      style,
      className,
      variant = DATE_PICKER_VARIANTS.FIELD,
      container = document.body,
      renderTrigger = defaultRenderTrigger,
      overlayProps = {},
      initialValueSelected = true,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const t = theme.tokens?.component?.datePicker;

    const target = useForwardedRef(ref);
    const [shouldShowSelection, setShouldShowSelection] =
      React.useState<boolean>(initialValueSelected);
    const dateRangePickerData = useDateRangePicker({
      variant,
      target,
      setShouldShowSelection,
      ...props,
    });
    const {
      context,
      open,
      targetProps,
      placement,
      getTriggerProps,
      arrowProps,
      overlayContainerProps,
    } = dateRangePickerData;

    return (
      <PickerOverlayPortal
        className={className}
        style={style}
        container={container}
        open={open}
        context={context}
        targetProps={targetProps}
        placement={placement}
        getTriggerProps={getTriggerProps}
        arrowProps={arrowProps}
        overlayContainerProps={overlayContainerProps}
        arrowColor={t?.arrow?.backgroundColor}
        variant={variant}
        renderTrigger={renderTrigger}
        renderOverlay={() => (
          <DatePickerOverlay
            range
            double={props.double}
            timeZone={props.timeZone}
            calendarProps={props.calendarProps}
            onMonthChange={props.onMonthChange}
            showOutsideDays={props.showOutsideDays}
            isDateSelectable={props.isDateSelectable}
            yearsToDisplay={props.yearsToDisplay}
            firstDayOfWeek={props.firstDayOfWeek}
            tooltipContentForDate={props.tooltipContentForDate}
            hasEventForDate={props.hasEventForDate}
            renderFooter={props.renderFooter}
            renderFooterActions={props.renderFooterActions}
            minDate={dateRangePickerData.minDate}
            maxDate={dateRangePickerData.maxDate}
            startDate={dateRangePickerData.startDate}
            endDate={dateRangePickerData.endDate}
            toggleOverlay={dateRangePickerData.toggleOverlay}
            onDateSelected={dateRangePickerData.onDateSelected}
            resetSelection={dateRangePickerData.resetSelection}
            shouldShowSelection={shouldShowSelection}
            {...overlayProps}
          />
        )}
      />
    );
  },
);

DateRangePicker.displayName = 'DateRangePicker';

DateRangePicker.propTypes = {
  ...datePickerCommonPropTypes,
  /** A selected start date for a calendar with range data (should be used with range=true) */
  startDate: PropTypes.instanceOf(Date),
  /** A selected end date for a calendar with range data (should be used with range=true) */
  endDate: PropTypes.instanceOf(Date),
  /** The initial start date for calendar with date range select */
  initialStartDate: PropTypes.instanceOf(Date),
  /** The initial end date for calendar with date range select */
  initialEndDate: PropTypes.instanceOf(Date),
  /** Whether we should allow range start and end to be the same date */
  allowSameStartAndEndDate: PropTypes.bool,
  /** Passed to the field, placeholder to display when there is no value - when passed in range mode, it will affect both start and end dates */
  placeholder: PropTypes.string,
  /** Passed to the field, placeholder to display when there is no startDate. this prop overrides `placeholder` */
  startDatePlaceholder: PropTypes.string,
  /** Passed to the field, placeholder to display when there is no endDate. this prop overrides `placeholder` */
  endDatePlaceholder: PropTypes.string,
  /** Render custom footer, executed in DateRangePicker */
  renderTrigger: PropTypes.func,
};

export default Object.assign(DateRangePicker, {
  VARIANTS: DATE_PICKER_VARIANTS,
  VALID_FORMATS: VALID_DATE_FORMATS,
  Footer,
  Trigger,
});
